import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';

const KEY = 'floreal_inspirations';

const InspirationContext = createContext(null);

export const InspirationProvider = ({ children }) => {
    const [items, setItems] = useState([]); // [{id, image_url, title, category}]

    useEffect(() => {
        try {
            const raw = window.localStorage.getItem(KEY);
            if (raw) setItems(JSON.parse(raw));
        } catch (err) {
            // Corrupted storage or quota exceeded — keep an empty state and warn in dev.
            if (process.env.NODE_ENV !== 'production') {
                console.warn('[InspirationContext] Failed to load inspirations from localStorage:', err);
            }
        }
    }, []);

    useEffect(() => {
        try {
            window.localStorage.setItem(KEY, JSON.stringify(items));
        } catch (err) {
            // Quota exceeded or privacy mode blocked storage — degrade gracefully.
            if (process.env.NODE_ENV !== 'production') {
                console.warn('[InspirationContext] Failed to persist inspirations to localStorage:', err);
            }
        }
    }, [items]);

    const isLiked = useCallback((id) => items.some((i) => i.id === id), [items]);

    const toggleLike = useCallback((item) => {
        setItems((prev) => {
            const exists = prev.find((p) => p.id === item.id);
            if (exists) return prev.filter((p) => p.id !== item.id);
            return [...prev, {
                id: item.id,
                image_url: item.image_url,
                title: item.title,
                category: item.category,
            }];
        });
    }, []);

    const remove = useCallback((id) => {
        setItems((prev) => prev.filter((p) => p.id !== id));
    }, []);

    const clear = useCallback(() => setItems([]), []);

    const value = useMemo(() => ({ items, isLiked, toggleLike, remove, clear }), [items, isLiked, toggleLike, remove, clear]);

    return <InspirationContext.Provider value={value}>{children}</InspirationContext.Provider>;
};

export const useInspirations = () => {
    const ctx = useContext(InspirationContext);
    if (!ctx) throw new Error('useInspirations must be used within InspirationProvider');
    return ctx;
};
